import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@/shims/dnd-kit-sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Mail, Calendar, Workflow, Zap, Filter, Send, Clock, CheckCircle2, AlertCircle,
  GripVertical, Plus, Settings, Play, Pause, Trash2, Copy, ChevronDown, ChevronRight,
  Database, Search, BarChart3, FileText, Sliders, Save, Brain, Sparkles, Globe,
  Target, GitBranch, Repeat, ArrowRight, Eye, Code, TestTube, Activity, Edit
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { cn } from '@/lib/utils';
import { createClient } from '@blinkdotnew/sdk';
import { toast } from 'sonner';
import { workflowCacheService } from '@/services/workflowCacheService';
import { safeJsonParse } from '@/lib/utils';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

interface WorkflowNode {
  id: string;
  type: 'trigger' | 'condition' | 'action' | 'ai' | 'loop' | 'branch';
  icon: any;
  title: string;
  description: string;
  config: Record<string, any>;
  isExpanded: boolean;
  status?: 'idle' | 'running' | 'success' | 'error';
  executionTime?: number;
  output?: any;
}

interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  nodes: WorkflowNode[];
  category: 'email' | 'productivity' | 'ai' | 'automation';
}

const enhancedNodes = [
  // Triggers
  { type: 'trigger', icon: Mail, title: 'Email Received', description: 'Trigger on new email', category: 'email' },
  { type: 'trigger', icon: Calendar, title: 'Schedule', description: 'Run on schedule (cron)', category: 'time' },
  { type: 'trigger', icon: Clock, title: 'Time-based', description: 'Trigger at specific time', category: 'time' },
  { type: 'trigger', icon: Globe, title: 'Webhook', description: 'External API trigger', category: 'api' },
  
  // AI-Powered Nodes
  { type: 'ai', icon: Brain, title: 'AI Classify', description: 'Classify with GPT-5', category: 'ai' },
  { type: 'ai', icon: Sparkles, title: 'AI Extract', description: 'Extract entities with AI', category: 'ai' },
  { type: 'ai', icon: Brain, title: 'AI Generate', description: 'Generate content', category: 'ai' },
  { type: 'ai', icon: Search, title: 'AI Research', description: 'Deep research with web search', category: 'ai' },
  { type: 'ai', icon: BarChart3, title: 'AI Analyze', description: 'Advanced analysis', category: 'ai' },
  
  // Conditions
  { type: 'condition', icon: Filter, title: 'Filter', description: 'Filter by conditions', category: 'logic' },
  { type: 'branch', icon: GitBranch, title: 'Branch', description: 'Split workflow path', category: 'logic' },
  { type: 'condition', icon: Target, title: 'Match Pattern', description: 'Regex pattern matching', category: 'logic' },
  
  // Actions
  { type: 'action', icon: Send, title: 'Send Email', description: 'Send email message', category: 'email' },
  { type: 'action', icon: Calendar, title: 'Create Event', description: 'Add calendar event', category: 'calendar' },
  { type: 'action', icon: Database, title: 'Store Data', description: 'Save to database', category: 'data' },
  { type: 'action', icon: Zap, title: 'HTTP Request', description: 'Call external API', category: 'api' },
  { type: 'action', icon: FileText, title: 'Generate Report', description: 'Create formatted report', category: 'output' },
  
  // Control Flow
  { type: 'loop', icon: Repeat, title: 'For Each', description: 'Iterate over items', category: 'control' },
  { type: 'action', icon: Clock, title: 'Wait', description: 'Delay execution', category: 'control' },
  { type: 'action', icon: Code, title: 'Custom Code', description: 'Run JavaScript code', category: 'advanced' }
];

const workflowTemplates: WorkflowTemplate[] = [
  {
    id: 'auto-reply',
    name: 'Smart Auto-Reply',
    description: 'AI-powered automatic email responses',
    category: 'email',
    nodes: [
      { id: '1', type: 'trigger', icon: Mail, title: 'Email Received', description: 'New email arrives', config: {}, isExpanded: false },
      { id: '2', type: 'ai', icon: Brain, title: 'AI Classify', description: 'Classify email intent', config: { model: 'gpt-5' }, isExpanded: false },
      { id: '3', type: 'condition', icon: Filter, title: 'Check Priority', description: 'Filter high priority', config: {}, isExpanded: false },
      { id: '4', type: 'ai', icon: Sparkles, title: 'AI Generate', description: 'Generate response', config: {}, isExpanded: false },
      { id: '5', type: 'action', icon: Send, title: 'Send Reply', description: 'Send automated reply', config: {}, isExpanded: false }
    ]
  },
  {
    id: 'research-digest',
    name: 'Research Digest',
    description: 'Daily AI research summary',
    category: 'ai',
    nodes: [
      { id: '1', type: 'trigger', icon: Clock, title: 'Daily at 9am', description: 'Scheduled trigger', config: { cron: '0 9 * * *' }, isExpanded: false },
      { id: '2', type: 'ai', icon: Search, title: 'Web Research', description: 'Research topics', config: { topics: [] }, isExpanded: false },
      { id: '3', type: 'ai', icon: BarChart3, title: 'Analyze Trends', description: 'Analyze findings', config: {}, isExpanded: false },
      { id: '4', type: 'action', icon: FileText, title: 'Generate Report', description: 'Create summary', config: {}, isExpanded: false },
      { id: '5', type: 'action', icon: Send, title: 'Email Report', description: 'Send to inbox', config: {}, isExpanded: false }
    ]
  },
  {
    id: 'meeting-prep',
    name: 'Meeting Preparation',
    description: 'Automated meeting research and briefing',
    category: 'productivity',
    nodes: [
      { id: '1', type: 'trigger', icon: Calendar, title: 'Before Meeting', description: '1 hour before', config: {}, isExpanded: false },
      { id: '2', type: 'ai', icon: Search, title: 'Research Attendees', description: 'Get background info', config: {}, isExpanded: false },
      { id: '3', type: 'ai', icon: Brain, title: 'Analyze Context', description: 'Review past emails', config: {}, isExpanded: false },
      { id: '4', type: 'ai', icon: Sparkles, title: 'Generate Brief', description: 'Create meeting brief', config: {}, isExpanded: false },
      { id: '5', type: 'action', icon: Send, title: 'Send Brief', description: 'Notify user', config: {}, isExpanded: false }
    ]
  }
];

function SortableNode({ node, onToggle, onUpdate, onDelete, onDuplicate }: any) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getNodeColor = () => {
    switch (node.type) {
      case 'trigger': return 'from-blue-500/20 to-cyan-500/20';
      case 'condition': return 'from-purple-500/20 to-pink-500/20';
      case 'action': return 'from-green-500/20 to-emerald-500/20';
      case 'ai': return 'from-amber-500/20 to-orange-500/20';
      case 'loop': return 'from-indigo-500/20 to-violet-500/20';
      case 'branch': return 'from-rose-500/20 to-red-500/20';
      default: return 'from-gray-500/20 to-slate-500/20';
    }
  };

  const getStatusIcon = () => {
    switch (node.status) {
      case 'running': return <Activity className="w-4 h-4 text-blue-500 animate-pulse" />;
      case 'success': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'error': return <AlertCircle className="w-4 h-4 text-red-500" />;
      default: return null;
    }
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="relative"
    >
      <div className={cn('glass-card rounded-xl p-4 bg-gradient-to-br', getNodeColor())}>
        <div className="flex items-start gap-3 mb-3">
          <div 
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-foreground/5 rounded"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="w-5 h-5 text-muted-foreground" />
          </div>
          
          <div className="p-2 glass rounded-lg">
            <node.icon className="w-5 h-5 text-primary" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-sm">{node.title}</h4>
              {getStatusIcon()}
              <Badge variant="outline" className="text-xs">{node.type}</Badge>
              {node.executionTime && (
                <Badge variant="secondary" className="text-xs">{node.executionTime}ms</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{node.description}</p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => onToggle(node.id)} className="h-8 w-8 p-0">
              {node.isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDuplicate(node.id)} className="h-8 w-8 p-0">
              <Copy className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(node.id)} className="h-8 w-8 p-0 text-destructive">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {node.isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3 pt-3 border-t border-border/30"
            >
              {/* AI Node Configuration */}
              {node.type === 'ai' && (
                <>
                  <div className="space-y-2">
                    <Label className="text-xs">AI Model</Label>
                    <Select value={node.config.model || 'gpt-5'} onValueChange={(value) => onUpdate(node.id, { ...node.config, model: value })}>
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gpt-5">GPT-5 (Advanced)</SelectItem>
                        <SelectItem value="gpt-4-mini">GPT-4 Mini (Fast)</SelectItem>
                        <SelectItem value="claude">Claude Sonnet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">
                      Prompt <span className="text-destructive">*</span>
                    </Label>
                    <Textarea
                      placeholder="AI prompt or instruction..."
                      value={node.config.prompt || ''}
                      onChange={(e) => onUpdate(node.id, { ...node.config, prompt: e.target.value })}
                      className={cn(
                        "min-h-[80px] text-xs",
                        !node.config.prompt && "border-destructive/50"
                      )}
                    />
                    {!node.config.prompt && (
                      <p className="text-xs text-destructive">Required field</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={node.config.useWebSearch}
                      onCheckedChange={(checked) => onUpdate(node.id, { ...node.config, useWebSearch: checked })}
                    />
                    <Label className="text-xs">Enable Web Search</Label>
                  </div>
                </>
              )}

              {/* Condition Node Configuration */}
              {node.type === 'condition' && (
                <>
                  <div className="space-y-2">
                    <Label className="text-xs">
                      Condition Field <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      placeholder="e.g., trigger.priority or node_ai-1.category"
                      value={node.config.condition?.field || ''}
                      onChange={(e) => onUpdate(node.id, { 
                        ...node.config, 
                        condition: { 
                          ...node.config.condition, 
                          field: e.target.value 
                        } 
                      })}
                      className={cn(
                        "h-8 text-xs",
                        !node.config.condition?.field && "border-destructive/50"
                      )}
                    />
                    {!node.config.condition?.field && (
                      <p className="text-xs text-destructive">Required field - specify which field to evaluate</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Operator</Label>
                    <Select 
                      value={node.config.condition?.operator || 'equals'} 
                      onValueChange={(value) => onUpdate(node.id, { 
                        ...node.config, 
                        condition: { 
                          ...node.config.condition, 
                          operator: value 
                        } 
                      })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="equals">Equals</SelectItem>
                        <SelectItem value="not_equals">Not Equals</SelectItem>
                        <SelectItem value="contains">Contains</SelectItem>
                        <SelectItem value="greater_than">Greater Than</SelectItem>
                        <SelectItem value="less_than">Less Than</SelectItem>
                        <SelectItem value="exists">Exists</SelectItem>
                        <SelectItem value="is_empty">Is Empty</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Value</Label>
                    <Input
                      placeholder="Value to compare against"
                      value={node.config.condition?.value || ''}
                      onChange={(e) => onUpdate(node.id, { 
                        ...node.config, 
                        condition: { 
                          ...node.config.condition, 
                          value: e.target.value 
                        } 
                      })}
                      className="h-8 text-xs"
                    />
                  </div>
                </>
              )}

              {/* Action Node Configuration - Send Email */}
              {node.type === 'action' && node.title === 'Send Email' && (
                <>
                  <div className="space-y-2">
                    <Label className="text-xs">
                      To <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      placeholder="Recipient email or {{trigger.from}}"
                      value={node.config.to || ''}
                      onChange={(e) => onUpdate(node.id, { ...node.config, to: e.target.value })}
                      className={cn(
                        "h-8 text-xs",
                        !node.config.to && "border-destructive/50"
                      )}
                    />
                    {!node.config.to && (
                      <p className="text-xs text-destructive">Required field</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">
                      Subject <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      placeholder="Email subject or Re: {{trigger.subject}}"
                      value={node.config.subject || ''}
                      onChange={(e) => onUpdate(node.id, { ...node.config, subject: e.target.value })}
                      className={cn(
                        "h-8 text-xs",
                        !node.config.subject && "border-destructive/50"
                      )}
                    />
                    {!node.config.subject && (
                      <p className="text-xs text-destructive">Required field</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Body</Label>
                    <Textarea
                      placeholder="Email body or {{node_ai-1.generatedText}}"
                      value={node.config.body || ''}
                      onChange={(e) => onUpdate(node.id, { ...node.config, body: e.target.value })}
                      className="min-h-[80px] text-xs"
                    />
                  </div>
                </>
              )}

              {/* Generic Configuration */}
              <div className="flex gap-2 pt-2">
                <Button size="sm" variant="outline" className="flex-1 gap-2">
                  <Settings className="w-3 h-3" />
                  Advanced
                </Button>
                <Button size="sm" variant="outline" className="flex-1 gap-2">
                  <TestTube className="w-3 h-3" />
                  Test
                </Button>
              </div>

              {/* Node Output Preview */}
              {node.output && (
                <div className="p-2 bg-accent/50 rounded text-xs">
                  <p className="font-medium mb-1">Output:</p>
                  <pre className="whitespace-pre-wrap">{JSON.stringify(node.output, null, 2).substring(0, 200)}...</pre>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

interface EnhancedWorkflowBuilderProps {
  workflowIdToLoad?: string | null;
  onWorkflowLoaded?: () => void;
}

export function EnhancedWorkflowBuilder({ workflowIdToLoad, onWorkflowLoaded }: EnhancedWorkflowBuilderProps = {}) {
  const [nodes, setNodes] = useState<WorkflowNode[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [currentWorkflow, setCurrentWorkflow] = useState<any>(null);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [user, setUser] = useState<any>(null);

  // Set up drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load user and workflows using auth state listener with caching
  useEffect(() => {
    const loadWorkflowsForUser = async (userId: string) => {
      try {
        // Check cache first
        const cached = workflowCacheService.get(userId);
        if (cached) {
          setWorkflows(cached);
          console.log(`✅ Loaded ${cached.length} workflow(s) from cache`);
          return;
        }

        // Cache miss - fetch from database
        const savedWorkflows = await blink.db.workflows.list({
          where: { userId },
          orderBy: { updatedAt: 'desc' }
        });
        
        if (savedWorkflows && savedWorkflows.length > 0) {
          setWorkflows(savedWorkflows);
          // Update cache
          workflowCacheService.set(userId, savedWorkflows);
          console.log(`✅ Loaded ${savedWorkflows.length} workflow(s) from DB and cached`);
        } else {
          setWorkflows([]);
          // Cache empty result
          workflowCacheService.set(userId, []);
        }
      } catch (error) {
        console.error('Error loading workflows:', error);
        // Don't show error toast if not authenticated
        if (error instanceof Error && !error.message.includes('Not authenticated')) {
          toast.error('Failed to load workflows');
        }
      }
    };
    
    const unsubscribe = blink.auth.onAuthStateChanged(async (state) => {
      if (state.user && !state.isLoading) {
        setUser(state.user);
        await loadWorkflowsForUser(state.user.id);
      } else if (!state.isLoading && !state.user) {
        setUser(null);
        setWorkflows([]);
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Handle workflow loading from parent component (Overview tab)
  useEffect(() => {
    if (workflowIdToLoad && user) {
      console.log(`🔄 Loading workflow ${workflowIdToLoad} from parent component`);
      loadWorkflowFromDB(workflowIdToLoad);
      onWorkflowLoaded?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowIdToLoad, user]);

  const loadWorkflows = async (userId?: string, forceRefresh = false) => {
    try {
      const currentUserId = userId || user?.id;
      if (!currentUserId) {
        console.log('⚠️ No user ID available for loading workflows');
        return;
      }

      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cached = workflowCacheService.get(currentUserId);
        if (cached) {
          setWorkflows(cached);
          console.log(`✅ Loaded ${cached.length} workflow(s) from cache`);
          return;
        }
      }
      
      // Fetch from database
      const savedWorkflows = await blink.db.workflows.list({
        where: { userId: currentUserId },
        orderBy: { updatedAt: 'desc' }
      });
      
      if (savedWorkflows && savedWorkflows.length > 0) {
        setWorkflows(savedWorkflows);
        // Update cache
        workflowCacheService.set(currentUserId, savedWorkflows);
        console.log(`✅ Loaded ${savedWorkflows.length} workflow(s) from DB and cached`);
      } else {
        setWorkflows([]);
        // Cache empty result
        workflowCacheService.set(currentUserId, []);
      }
    } catch (error) {
      console.error('Error loading workflows:', error);
      // Don't show error toast if not authenticated
      if (error instanceof Error && !error.message.includes('Not authenticated')) {
        toast.error('Failed to load workflows');
      }
    }
  };

  const saveWorkflow = async () => {
    try {
      if (!user) {
        toast.error('Please sign in to save workflows');
        return;
      }

      if (nodes.length === 0) {
        toast.error('Cannot save empty workflow');
        return;
      }

      const workflowData = {
        id: currentWorkflow?.id || `wf_${Date.now()}`,
        userId: user.id,
        name: currentWorkflow?.name || 'Untitled Workflow',
        description: currentWorkflow?.description || '',
        trigger: nodes[0]?.title || 'manual',
        actions: JSON.stringify(nodes),
        isActive: "1",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Save or update workflow in database
      if (currentWorkflow?.id) {
        // Update existing workflow
        await blink.db.workflows.update(currentWorkflow.id, {
          name: workflowData.name,
          description: workflowData.description,
          trigger: workflowData.trigger,
          actions: workflowData.actions,
          updatedAt: workflowData.updatedAt
        });
        
        // Optimistic cache update
        workflowCacheService.updateWorkflow(user.id, currentWorkflow.id, {
          name: workflowData.name,
          description: workflowData.description,
          trigger: workflowData.trigger,
          actions: workflowData.actions,
          updatedAt: workflowData.updatedAt,
          userId: user.id,
          isActive: "1",
          id: currentWorkflow.id,
          createdAt: workflowData.createdAt
        });
        
        toast.success('Workflow updated successfully');
      } else {
        // Create new workflow
        await blink.db.workflows.create(workflowData);
        
        // Optimistic cache update
        workflowCacheService.addWorkflow(user.id, workflowData);
        
        setCurrentWorkflow({ id: workflowData.id, name: workflowData.name, description: workflowData.description });
        toast.success('Workflow saved successfully');
      }

      // Register workflow with execution engine for automatic triggering
      try {
        const { workflowExecutionEngine } = await import('@/services/workflowExecutionEngine');
        
        const workflowDef = {
          id: workflowData.id,
          name: workflowData.name,
          description: workflowData.description,
          nodes: nodes.map(node => ({
            id: node.id,
            type: node.type,
            title: node.title,
            description: node.description,
            config: node.config
          })),
          enabled: true,
          userId: user.id
        };
        
        workflowExecutionEngine.registerWorkflow(workflowDef);
        console.log(`✅ Registered workflow "${workflowData.name}" for automatic execution`);
      } catch (error) {
        console.error('Failed to register workflow with execution engine:', error);
        // Don't fail the save if registration fails
      }

      // Reload workflows list to sync with database
      await loadWorkflows(user.id);
    } catch (error) {
      console.error('Error saving workflow:', error);
      toast.error('Failed to save workflow');
      
      // Invalidate cache on error to force refresh
      if (user) {
        workflowCacheService.invalidate(user.id);
      }
    }
  };

  const createNewWorkflow = () => {
    if (nodes.length > 0) {
      const confirmNew = window.confirm('You have unsaved changes. Create new workflow?');
      if (!confirmNew) return;
    }
    
    setNodes([]);
    setCurrentWorkflow(null);
    toast.success('New workflow created');
  };

  const loadTemplate = (template: WorkflowTemplate) => {
    setNodes(template.nodes);
    setCurrentWorkflow({ name: template.name, description: template.description });
    toast.success(`Loaded: ${template.name}`);
  };

  const loadWorkflowFromDB = async (workflowId: string) => {
    try {
      console.log('📥 Loading workflow from DB:', workflowId);
      
      if (!user) {
        console.error('❌ No user - cannot load workflow');
        toast.error('Please sign in to load workflows');
        return;
      }

      console.log('🔍 Fetching workflow for user:', user.id, 'workflow:', workflowId);

      const savedWorkflows = await blink.db.workflows.list({
        where: { userId: user.id, id: workflowId },
        limit: 1
      });

      console.log('📦 Fetch result:', savedWorkflows?.length || 0, 'workflows found');

      if (!savedWorkflows || savedWorkflows.length === 0) {
        console.error('❌ Workflow not found in DB:', workflowId);
        toast.error('Workflow not found');
        return;
      }

      const workflow = savedWorkflows[0];
      console.log('✅ Workflow found:', workflow.name);
      
      // Parse the actions (which are stored as JSON string of nodes)
      let parsedNodes = [];
      try {
        parsedNodes = safeJsonParse(workflow.actions);
        console.log('✅ Parsed', parsedNodes.length, 'nodes from workflow');
      } catch (e) {
        console.error('❌ Failed to parse workflow actions:', e);
        toast.error('Failed to load workflow - invalid format');
        return;
      }

      // Load nodes into builder
      console.log('🔄 Loading nodes into builder...');
      setNodes(parsedNodes.map((node: any) => ({
        ...node,
        isExpanded: false,
        status: 'idle'
      })));

      setCurrentWorkflow({
        id: workflow.id,
        name: workflow.name,
        description: workflow.description || ''
      });

      console.log('✅ Workflow loaded successfully into builder');
      toast.success(`Loaded workflow: ${workflow.name}`);
    } catch (error) {
      console.error('❌ Error loading workflow:', error);
      toast.error('Failed to load workflow');
    }
  };

  const runWorkflow = async () => {
    if (!user) {
      toast.error('Please sign in to run workflows');
      return;
    }
    
    if (!currentWorkflow?.id && nodes.length === 0) {
      toast.error('Please create or load a workflow first');
      return;
    }

    // Validate workflow before execution - CRITICAL: Stop execution if validation fails
    const validation = validateWorkflow();
    if (!validation.valid) {
      // Show each validation error to the user
      validation.errors.forEach((error, index) => {
        setTimeout(() => {
          toast.error('Workflow Validation Error', {
            description: error,
            duration: 6000
          });
        }, index * 100); // Stagger toasts so they're all visible
      });
      
      console.error('❌ Workflow validation failed. Cannot run workflow.', validation.errors);
      
      // DO NOT proceed with execution
      return;
    }
    
    setIsRunning(true);
    setExecutionProgress(0);
    
    try {

      // Import workflow execution engine
      const { workflowExecutionEngine } = await import('@/services/workflowExecutionEngine');
      
      // Build workflow definition from current nodes
      const workflowDef = {
        id: currentWorkflow?.id || `wf_${Date.now()}`,
        name: currentWorkflow?.name || 'Untitled Workflow',
        description: currentWorkflow?.description || '',
        nodes: nodes.map(node => ({
          id: node.id,
          type: node.type,
          title: node.title,
          description: node.description,
          config: node.config
        })),
        enabled: true,
        userId: user.id
      };
      
      console.log('🚀 Executing workflow:', workflowDef.name);
      
      // Execute workflow with real engine
      const result = await workflowExecutionEngine.executeWorkflow(workflowDef, {
        userId: user.id,
        trigger: {
          type: 'manual',
          timestamp: new Date().toISOString()
        }
      });
      
      console.log('✅ Workflow execution result:', result);
      
      // Update UI with real execution results
      if (result.results && Array.isArray(result.results)) {
        result.results.forEach((nodeResult: any, index: number) => {
          setNodes(prev => prev.map((n, i) => 
            i === index ? {
              ...n,
              status: nodeResult.status === 'success' ? 'success' : nodeResult.status === 'error' ? 'error' : 'idle',
              executionTime: nodeResult.endTime ? 
                new Date(nodeResult.endTime).getTime() - new Date(nodeResult.startTime).getTime() : 
                undefined,
              output: nodeResult.output
            } : n
          ));
          
          setExecutionProgress(((index + 1) / nodes.length) * 100);
        });
      }
      
      if (result.success) {
        toast.success('Workflow completed successfully');
      } else {
        toast.error('Workflow execution completed with errors');
      }
    } catch (error) {
      console.error('Workflow execution error:', error);
      toast.error(`Workflow execution failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      // Mark all nodes as error
      setNodes(prev => prev.map(n => ({ ...n, status: 'error' })));
    } finally {
      setIsRunning(false);
      setTimeout(() => {
        setNodes(prev => prev.map(n => ({ ...n, status: 'idle' })));
      }, 3000);
    }
  };

  // Drag and drop handler
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setNodes((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const toggleNode = (id: string) => {
    setNodes(nodes.map(node => node.id === id ? { ...node, isExpanded: !node.isExpanded } : node));
  };

  const updateNode = (id: string, config: any) => {
    setNodes(nodes.map(node => node.id === id ? { ...node, config } : node));
  };

  const deleteNode = (id: string) => {
    setNodes(nodes.filter(node => node.id !== id));
  };

  const duplicateNode = (id: string) => {
    const node = nodes.find(n => n.id === id);
    if (node) {
      const newNode = { ...node, id: `${node.id}_${Date.now()}`, isExpanded: false };
      const index = nodes.findIndex(n => n.id === id);
      setNodes([...nodes.slice(0, index + 1), newNode, ...nodes.slice(index + 1)]);
    }
  };

  const addNode = (template: any) => {
    const newNode: WorkflowNode = {
      id: `node_${Date.now()}`,
      type: template.type,
      icon: template.icon,
      title: template.title,
      description: template.description,
      config: getDefaultConfig(template.type, template.title),
      isExpanded: true,
      status: 'idle'
    };
    setNodes([...nodes, newNode]);
    setShowPalette(false);
  };

  const getDefaultConfig = (type: string, title: string): Record<string, any> => {
    // Provide sensible defaults based on node type
    switch (type) {
      case 'ai':
        return {
          model: 'gpt-5',
          prompt: '',
          maxTokens: 1000,
          useWebSearch: false
        };
      case 'action':
        if (title === 'Send Email') {
          return {
            to: '{{trigger.from}}',
            subject: 'Re: {{trigger.subject}}',
            body: ''
          };
        }
        return {};
      case 'condition':
        return {
          condition: {
            field: '',
            operator: 'equals',
            value: ''
          }
        };
      default:
        return {};
    }
  };

  const validateWorkflow = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (nodes.length === 0) {
      errors.push('Workflow must have at least one node');
      return { valid: false, errors };
    }

    // Check if first node is a trigger
    if (nodes[0].type !== 'trigger') {
      errors.push('First node must be a trigger');
    }

    // Validate each node configuration
    nodes.forEach((node, index) => {
      if (node.type === 'ai' && !node.config.prompt) {
        errors.push(`Node ${index + 1} (${node.title}): AI prompt is required`);
      }

      if (node.type === 'action' && node.title === 'Send Email') {
        if (!node.config.to) {
          errors.push(`Node ${index + 1} (${node.title}): Recipient email is required`);
        }
        if (!node.config.subject) {
          errors.push(`Node ${index + 1} (${node.title}): Email subject is required`);
        }
      }

      if (node.type === 'condition' && !node.config.condition?.field) {
        errors.push(`Node ${index + 1} (${node.title}): Condition field is required`);
      }
    });

    return { valid: errors.length === 0, errors };
  };

  const categories = [...new Set(enhancedNodes.map(n => n.category))];
  const filteredNodes = selectedCategory === 'all' 
    ? enhancedNodes 
    : enhancedNodes.filter(n => n.category === selectedCategory);

  // Get current validation status
  const validationStatus = validateWorkflow();
  const hasValidationErrors = !validationStatus.valid && nodes.length > 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 glass rounded-lg">
              <Workflow className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {currentWorkflow?.name || 'Enhanced Workflow Builder'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {currentWorkflow?.description || 'AI-powered automation engine'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={isRunning ? 'default' : 'secondary'} className="gap-2">
              {isRunning ? <Activity className="w-3 h-3 animate-pulse" /> : <Eye className="w-3 h-3" />}
              {isRunning ? 'Running' : 'Idle'}
            </Badge>
            {hasValidationErrors && (
              <Badge variant="destructive" className="gap-2">
                <AlertCircle className="w-3 h-3" />
                {validationStatus.errors.length} Error{validationStatus.errors.length > 1 ? 's' : ''}
              </Badge>
            )}
            {currentWorkflow && (
              <Badge variant="outline" className="gap-2">
                {currentWorkflow.name}
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={createNewWorkflow}>
              <Plus className="w-4 h-4 mr-2" />
              New
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowPalette(!showPalette)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Node
            </Button>
            <Button variant="outline" size="sm" onClick={saveWorkflow} disabled={nodes.length === 0}>
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
            <Button size="sm" onClick={runWorkflow} disabled={isRunning || nodes.length === 0}>
              {isRunning ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
              {isRunning ? 'Running...' : 'Run'}
            </Button>
          </div>
        </div>

        {isRunning && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Execution Progress</span>
              <span className="text-xs font-medium">{Math.round(executionProgress)}%</span>
            </div>
            <Progress value={executionProgress} className="h-2" />
          </div>
        )}
      </div>

      {/* Templates */}
      <Tabs defaultValue="builder" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="builder">Builder</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-2">
          {workflowTemplates.map(template => (
            <Card key={template.id} className="p-4 cursor-pointer hover:bg-accent/50 transition-all" onClick={() => loadTemplate(template)}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold">{template.name}</h3>
                  <p className="text-sm text-muted-foreground">{template.description}</p>
                  <Badge variant="outline" className="mt-2 text-xs">{template.nodes.length} nodes</Badge>
                </div>
                <Button variant="ghost" size="sm">Load</Button>
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="history" className="space-y-2">
          {workflows.length === 0 ? (
            <div className="glass-card rounded-xl p-12 text-center">
              <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No saved workflows</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create and save your first workflow to see it here
              </p>
              <Button onClick={() => window.location.hash = '#builder'}>
                <Plus className="w-4 h-4 mr-2" />
                Create Workflow
              </Button>
            </div>
          ) : (
            workflows.map(workflow => {
              let nodeCount = 0;
              try {
                const parsed = safeJsonParse(workflow.actions);
                nodeCount = Array.isArray(parsed) ? parsed.length : 0;
              } catch (e) {
                nodeCount = 0;
              }
              
              return (
                <Card key={workflow.id} className="p-4 hover:bg-accent/50 transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{workflow.name}</h3>
                        <Badge variant={Number(workflow.isActive) > 0 ? 'default' : 'secondary'} className="text-xs">
                          {Number(workflow.isActive) > 0 ? 'Active' : 'Paused'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{workflow.description || 'No description'}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <GitBranch className="w-3 h-3" />
                          {nodeCount} nodes
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {workflow.lastRunAt ? new Date(workflow.lastRunAt).toLocaleDateString() : 'Never run'}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => loadWorkflowFromDB(workflow.id)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Load
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={async () => {
                          if (!user) {
                            toast.error('Please sign in to delete workflows');
                            return;
                          }
                          
                          const confirmed = window.confirm(`Delete "${workflow.name}"?`);
                          if (confirmed) {
                            try {
                              // Optimistic update - remove from UI immediately
                              workflowCacheService.removeWorkflow(user.id, workflow.id);
                              setWorkflows(prev => prev.filter(w => w.id !== workflow.id));
                              
                              // Delete from database
                              await blink.db.workflows.delete(workflow.id);
                              toast.success('Workflow deleted');
                              
                              console.log(`✅ Deleted workflow: ${workflow.name}`);
                            } catch (error) {
                              console.error('Failed to delete workflow:', error);
                              toast.error('Failed to delete workflow');
                              
                              // Revert optimistic update on error
                              workflowCacheService.invalidate(user.id);
                              await loadWorkflows(user.id, true); // Force refresh
                            }
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </TabsContent>

        <TabsContent value="builder">
          <div className="grid grid-cols-12 gap-4">
            {/* Node Palette */}
            <AnimatePresence>
              {showPalette && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="col-span-3 space-y-2"
                >
                  <div className="flex gap-1 flex-wrap mb-2">
                    <Button
                      variant={selectedCategory === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedCategory('all')}
                      className="text-xs"
                    >
                      All
                    </Button>
                    {categories.map(cat => (
                      <Button
                        key={cat}
                        variant={selectedCategory === cat ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedCategory(cat)}
                        className="text-xs capitalize"
                      >
                        {cat}
                      </Button>
                    ))}
                  </div>
                  
                  <h3 className="text-sm font-semibold px-2">Available Nodes</h3>
                  {filteredNodes.map(template => (
                    <motion.div
                      key={template.title}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="glass-card rounded-lg p-3 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                      onClick={() => addNode(template)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <template.icon className="w-4 h-4 text-primary" />
                        <p className="text-sm font-medium">{template.title}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">{template.description}</p>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Workflow Canvas */}
            <div className={cn('space-y-4', showPalette ? 'col-span-9' : 'col-span-12')}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={nodes.map(n => n.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {nodes.map((node, index) => (
                    <div key={node.id}>
                      <SortableNode
                        node={node}
                        onToggle={toggleNode}
                        onUpdate={updateNode}
                        onDelete={deleteNode}
                        onDuplicate={duplicateNode}
                      />
                      {index < nodes.length - 1 && (
                        <div className="flex justify-center py-2">
                          <ArrowRight className="w-4 h-4 text-primary" />
                        </div>
                      )}
                    </div>
                  ))}
                </SortableContext>
              </DndContext>

              {nodes.length === 0 && (
                <div className="glass-card rounded-xl p-12 text-center">
                  <Workflow className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No workflow nodes yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Start by adding nodes or loading a template
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Button onClick={() => setShowPalette(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Node
                    </Button>
                    <Button variant="outline">
                      <Eye className="w-4 h-4 mr-2" />
                      Browse Templates
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default EnhancedWorkflowBuilder;
